import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  RefreshCw,
  Search,
  Filter,
  Trash2,
  Eye,
  Clock,
  User,
  Globe,
  Server,
  Database,
  LogIn,
  ChevronLeft,
  ChevronRight,
  AlertCircle
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL;

const ErrorLogsPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [errorType, setErrorType] = useState('all');
  const [resolved, setResolved] = useState('all');
  const [search, setSearch] = useState('');
  
  // Detail view
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };
  const pageSize = 25;

  useEffect(() => {
    fetchData();
  }, [page, errorType, resolved]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString()
      });
      
      if (errorType !== 'all') params.append('error_type', errorType);
      if (resolved !== 'all') params.append('resolved', resolved === 'resolved' ? 'true' : 'false');
      if (search) params.append('search', search);
      
      const [logsRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/admin/error-logs?${params}`, { headers }),
        axios.get(`${API}/api/admin/error-logs/stats`, { headers })
      ]);
      
      setLogs(logsRes.data.logs);
      setTotal(logsRes.data.total);
      setTotalPages(Math.ceil(logsRes.data.total / pageSize));
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Failed to load error logs');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchData();
  };

  const resolveError = async (errorId) => {
    try {
      await axios.put(`${API}/api/admin/error-logs/${errorId}/resolve`, {}, { headers });
      toast.success('Error marked as resolved');
      fetchData();
    } catch (error) {
      toast.error('Failed to resolve error');
    }
  };

  const deleteError = async (errorId) => {
    if (!window.confirm('Delete this error log?')) return;
    
    try {
      await axios.delete(`${API}/api/admin/error-logs/${errorId}`, { headers });
      toast.success('Error log deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete error');
    }
  };

  const clearOldLogs = async () => {
    if (!window.confirm('Delete all error logs older than 30 days?')) return;
    
    try {
      const res = await axios.delete(`${API}/api/admin/error-logs/clear-old?days=30`, { headers });
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to clear old logs');
    }
  };

  const getErrorTypeIcon = (type) => {
    switch (type) {
      case 'login_failure': return <LogIn className="w-4 h-4" />;
      case 'api_error': return <Server className="w-4 h-4" />;
      case 'database_error': return <Database className="w-4 h-4" />;
      case 'system_error': return <AlertCircle className="w-4 h-4" />;
      default: return <AlertTriangle className="w-4 h-4" />;
    }
  };

  const getErrorTypeColor = (type) => {
    switch (type) {
      case 'login_failure': return 'bg-amber-100 text-amber-700';
      case 'api_error': return 'bg-red-100 text-red-700';
      case 'database_error': return 'bg-purple-100 text-purple-700';
      case 'system_error': return 'bg-slate-100 text-slate-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    // dd/MM/yyyy HH:mm:ss in NZ time
    return new Intl.DateTimeFormat('en-NZ', {
      timeZone: 'Pacific/Auckland',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(date);
  };

  if (loading && logs.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-screen" data-testid="error-logs-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50/30 p-4 md:p-6 lg:p-8" data-testid="error-logs-page">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-red-500 to-rose-600 rounded-xl shadow-lg">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Error Logs</h1>
              <p className="text-slate-500">Monitor production errors and login failures</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchData} data-testid="refresh-logs-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button variant="outline" onClick={clearOldLogs} className="text-red-600">
              <Trash2 className="w-4 h-4 mr-2" />
              Clear Old
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="border-0 shadow-md">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-600">{stats.total_errors_24h}</p>
                    <p className="text-xs text-slate-500">Errors (24h)</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <LogIn className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-600">{stats.login_failures_24h}</p>
                    <p className="text-xs text-slate-500">Login Failures (24h)</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-rose-100 rounded-lg">
                    <Server className="w-5 h-5 text-rose-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-rose-600">{stats.api_errors_24h}</p>
                    <p className="text-xs text-slate-500">API Errors (24h)</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Database className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-purple-600">{stats.database_errors_24h}</p>
                    <p className="text-xs text-slate-500">DB Errors (24h)</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-100 rounded-lg">
                    <Clock className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-600">{stats.total_errors_7d}</p>
                    <p className="text-xs text-slate-500">Total (7 days)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="border-0 shadow-md">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="text-sm text-slate-500 mb-1 block">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by message, email, endpoint..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
              </div>
              
              <div className="w-40">
                <label className="text-sm text-slate-500 mb-1 block">Type</label>
                <Select value={errorType} onValueChange={(v) => { setErrorType(v); setPage(1); }}>
                  <SelectTrigger data-testid="type-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="login_failure">Login Failures</SelectItem>
                    <SelectItem value="api_error">API Errors</SelectItem>
                    <SelectItem value="database_error">Database Errors</SelectItem>
                    <SelectItem value="system_error">System Errors</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="w-40">
                <label className="text-sm text-slate-500 mb-1 block">Status</label>
                <Select value={resolved} onValueChange={(v) => { setResolved(v); setPage(1); }}>
                  <SelectTrigger data-testid="status-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="unresolved">Unresolved</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Button onClick={handleSearch} data-testid="search-btn">
                <Filter className="w-4 h-4 mr-2" />
                Apply
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Error Logs Table */}
        <Card className="border-0 shadow-lg" data-testid="error-logs-table">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Error Logs ({total})</span>
              <div className="flex items-center gap-2 text-sm font-normal">
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span>Page {page} of {totalPages}</span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {logs.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
                <p className="font-medium">No errors found</p>
                <p className="text-sm">Your system is running smoothly!</p>
              </div>
            ) : (
              <div className="space-y-2">
                {logs.map((log) => (
                  <div 
                    key={log.id}
                    className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                      log.resolved ? 'bg-slate-50 border-slate-200' : 'bg-white border-red-100 hover:border-red-200'
                    }`}
                    data-testid={`error-log-${log.id}`}
                  >
                    <div className="flex items-start gap-4 flex-1 min-w-0">
                      <div className={`p-2 rounded-lg ${getErrorTypeColor(log.error_type)}`}>
                        {getErrorTypeIcon(log.error_type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={getErrorTypeColor(log.error_type)}>
                            {log.error_type.replace('_', ' ')}
                          </Badge>
                          {log.resolved && (
                            <Badge className="bg-green-100 text-green-700">Resolved</Badge>
                          )}
                          {log.status_code && (
                            <Badge variant="outline">{log.status_code}</Badge>
                          )}
                        </div>
                        <p className="font-medium text-sm mt-1 truncate">{log.message}</p>
                        <div className="flex items-center gap-4 mt-1 text-xs text-slate-500 flex-wrap">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDistanceToNow(new Date(log.timestamp), { addSuffix: true })}
                          </span>
                          {log.user_email && (
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {log.user_email}
                            </span>
                          )}
                          {log.ip_address && (
                            <span className="flex items-center gap-1">
                              <Globe className="w-3 h-3" />
                              {log.ip_address}
                            </span>
                          )}
                          {log.endpoint && (
                            <span className="font-mono">{log.endpoint}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => { setSelectedLog(log); setShowDetail(true); }}
                        data-testid={`view-detail-${log.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {!log.resolved && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-green-600"
                          onClick={() => resolveError(log.id)}
                          data-testid={`resolve-${log.id}`}
                        >
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                      )}
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-red-600"
                        onClick={() => deleteError(log.id)}
                        data-testid={`delete-${log.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Most Common Errors */}
        {stats && stats.most_common_errors.length > 0 && (
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="text-lg">Most Common Errors (Last 7 Days)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {stats.most_common_errors.map((err, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Badge className={getErrorTypeColor(err.error_type)}>
                        {err.error_type.replace('_', ' ')}
                      </Badge>
                      <span className="text-sm truncate max-w-md">{err.message}</span>
                    </div>
                    <Badge variant="outline">{err.count}x</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Error Detail Dialog */}
        <Dialog open={showDetail} onOpenChange={setShowDetail}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="error-detail-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                Error Details
              </DialogTitle>
              <DialogDescription>
                Full information about this error
              </DialogDescription>
            </DialogHeader>
            {selectedLog && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-slate-500">Type</label>
                    <p className="font-medium">{selectedLog.error_type}</p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Status Code</label>
                    <p className="font-medium">{selectedLog.status_code || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Timestamp</label>
                    <p className="font-medium">{formatDate(selectedLog.timestamp)}</p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">IP Address</label>
                    <p className="font-medium">{selectedLog.ip_address || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">User Email</label>
                    <p className="font-medium">{selectedLog.user_email || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Endpoint</label>
                    <p className="font-medium font-mono text-sm">{selectedLog.endpoint || 'N/A'}</p>
                  </div>
                </div>
                
                <div>
                  <label className="text-xs text-slate-500">Message</label>
                  <p className="font-medium bg-red-50 p-3 rounded-lg text-red-800">{selectedLog.message}</p>
                </div>
                
                {selectedLog.user_agent && (
                  <div>
                    <label className="text-xs text-slate-500">User Agent</label>
                    <p className="text-sm font-mono bg-slate-100 p-2 rounded text-xs break-all">{selectedLog.user_agent}</p>
                  </div>
                )}
                
                {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
                  <div>
                    <label className="text-xs text-slate-500">Additional Details</label>
                    <pre className="text-xs font-mono bg-slate-100 p-3 rounded-lg overflow-x-auto">
                      {JSON.stringify(selectedLog.details, null, 2)}
                    </pre>
                  </div>
                )}
                
                {selectedLog.stack_trace && (
                  <div>
                    <label className="text-xs text-slate-500">Stack Trace</label>
                    <pre className="text-xs font-mono bg-slate-900 text-green-400 p-3 rounded-lg overflow-x-auto max-h-60">
                      {selectedLog.stack_trace}
                    </pre>
                  </div>
                )}
                
                {selectedLog.resolved && (
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-700">
                      Resolved by {selectedLog.resolved_by} on {formatDate(selectedLog.resolved_at)}
                    </p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ErrorLogsPage;
